import { FC, useMemo } from 'react';
import { EstimateRecordIssue } from '../../../redux/estimateReportSlice';
import { IssuesTables } from './IssuesTables';
import { Link, Paper, Typography } from '@mui/material';
import { readableTime } from '../../../utils/readableTime';
import { IssueStateLabel } from './IssueStateLabel';
import { dateFilter } from '../../../utils/trackingDateUtils';

interface EstimateEpicInfoProps {
  sprintName: string;
  sprint: EstimateRecordIssue;
  skipZeroTasks: boolean;
  from: Date;
  to: Date;
  showIssues: 'All' | 'Closed' | 'Open';
}

export const EstimateStatus: FC<{ estimate: number; actual?: number; total: number }> = ({
  estimate,
  actual,
  total,
}) => {
  const hasActual = actual !== undefined;
  let color = '#9c27b0';
  if (estimate > total) color = '#5cff59';
  if (estimate < total) color = '#f44336';
  if (estimate === total) color = '#4caf50';
  return (
    <Paper
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'baseline',
        padding: 1,
        marginBottom: 2,
        background: color,
      }}
    >
      <Typography variant="h6">
        {hasActual ? 'A/' : ''}T/E: {hasActual ? readableTime(actual) : ''} /{' '}
        {readableTime(total)} / {readableTime(estimate)}{' '}
      </Typography>
    </Paper>
  );
};

export const EstimateEpicInfo: FC<EstimateEpicInfoProps> = ({
  skipZeroTasks,
  sprintName,
  from,
  to,
  sprint,
  showIssues,
}) => {
  const actual = useMemo(() => {
    const allTimeLogs = sprint.devIssues
      .flatMap((i) => i.timeLogs)
      .filter((i) => dateFilter(i.spentAt, from, to));
    return allTimeLogs.reduce((acc, i) => acc + i.timeSpent, 0) * 60 * 60;
  }, [sprint, from, to]);
  if (skipZeroTasks && sprint.estimate === 0 && sprint.total === 0) return <></>;
  if (showIssues === 'Closed' && sprint.pmIssue.state !== 'closed') return <></>;
  if (showIssues === 'Open' && sprint.pmIssue.state === 'closed') return <></>;
  return (
    <Paper
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'baseline',
        padding: 3,
        marginBottom: 2,
      }}
    >
      <Typography variant="h6">
        <Link target="_blank" rel="noopener noreferrer" href={sprint.pmIssue.webUrl}>
          {sprint.pmIssue.title}
        </Link>
      </Typography>
      <EstimateStatus actual={actual} total={sprint.total} estimate={sprint.estimate} />
      <IssueStateLabel issue={sprint.pmIssue} />
      {(sprint.pmIssue.time_stats?.total_time_spent ?? 0) > 0 && (
        <Typography variant="h6">
          PM Total: {readableTime(sprint.pmIssue.time_stats?.total_time_spent ?? 0)}
        </Typography>
      )}
      <IssuesTables
        from={from}
        to={to}
        issues={sprint.devIssues}
        pmIssue={sprint.pmIssue}
      />
    </Paper>
  );
};
